import { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Controller, type ControllerRenderProps, type FieldValues } from 'react-hook-form';
import {
    $getRoot,
    $createParagraphNode,
    $createTextNode,
    $getNodeByKey,
    $getSelection,
    $isRangeSelection,
    KEY_DOWN_COMMAND,
    PASTE_COMMAND,
    COMMAND_PRIORITY_CRITICAL,
} from 'lexical';

import { MathSymbols } from '@/src/lib';
import { MathPlugin } from './plugins/MathPlugin';
import { FractionNode, ExponentNode, RootNode, OverlineNode } from './components/MathNodes';
import MathKeypadPlugin from './plugins/MathKeypadPlugin';
import { BlockEditorEnterPlugin } from './plugins/BlockEnterPlugin';
import { ErrorMessage } from '@/src/ui';
import { AllowedKeysContext } from './components/AllowedKeysContext';
import ArrowKeyNavigationPlugin from './plugins/ArrowNavigationPlugin';
import './DynamicEquationEditorStyles.css';
import { compressEditorState, decompressEditorState, isEquationEditorEmpty } from '../utils';

const theme = {};

export type CDEEFieldType = ControllerRenderProps<FieldValues, string>;

interface ControlledDynamicEquationEditorProps {
    control: any;
    name: string;
    initialValue?: string;
    contentKey?: string;
    setOutput?: (val: string, field: ControllerRenderProps<FieldValues, string>) => void;
    allowedKeys?: string[];
    showKeypad?: boolean;
    label?: string;
    style?: React.CSSProperties;
    error?: any;
    disabled?: boolean;
}

export function ControlledDynamicEquationEditor({
    control,
    name,
    initialValue = '',
    contentKey,
    setOutput = () => {},
    showKeypad = false,
    allowedKeys = [],
    label = '',
    style = {},
    error = '',
    disabled = false,
}: ControlledDynamicEquationEditorProps) {
    return (
        <Controller
            name={name}
            control={control}
            defaultValue={initialValue ?? ''}
            render={({ field }) => (
                <DynamicEquationEditor
                    initialValue={initialValue}
                    contentKey={contentKey}
                    setOutput={(val) => {
                        field.onChange(val);
                        setOutput?.(val, field);
                    }}
                    showKeypad={showKeypad}
                    allowedKeys={allowedKeys}
                    label={label}
                    style={style}
                    error={error}
                    disabled={disabled}
                />
            )}
        />
    );
}

interface DynamicEquationEditorProps {
    initialValue?: string;
    contentKey?: string;
    setOutput: (output: string) => void;
    showKeypad?: boolean;
    allowedKeys?: string[];
    label?: string;
    style?: React.CSSProperties;
    error?: any;
    disabled?: boolean;
}

export function DynamicEquationEditor({
    initialValue = '',
    contentKey,
    setOutput,
    showKeypad = false,
    allowedKeys = [],
    label = '',
    style = {},
    error = '',
    disabled = false,
}: DynamicEquationEditorProps) {
    const allowedKeysMemo = useMemo(() => allowedKeys, [allowedKeys]);

    const initialConfig = useMemo(
        () => ({
            namespace: 'pm-equation-editor',
            theme,
            onError(_error: Error) {
                console.error(_error);
            },
            editorState: undefined,
            nodes: [FractionNode, ExponentNode, RootNode, OverlineNode],
        }),
        []
    );

    const key = contentKey || 'default';

    return (
        <div className="pm-math-editor-wrapper" style={{ display: 'grid', gap: 8, ...style }}>
            <AllowedKeysContext.Provider value={allowedKeysMemo}>
                <LexicalComposer key={key} initialConfig={initialConfig}>
                    <DynamicEquationEditorInner
                        initialValue={initialValue}
                        contentKey={contentKey}
                        setOutput={setOutput}
                        showKeypad={showKeypad}
                        allowedKeys={allowedKeys}
                        label={label}
                        error={error}
                        disabled={disabled}
                    />
                </LexicalComposer>
            </AllowedKeysContext.Provider>
        </div>
    );
}

interface DynamicEquationEditorInnerProps {
    initialValue: string;
    contentKey?: string;
    setOutput: (output: string) => void;
    showKeypad: boolean;
    allowedKeys: string[];
    label: string;
    error: any;
    disabled: boolean;
}

function DynamicEquationEditorInner({
    initialValue,
    contentKey,
    setOutput,
    showKeypad,
    allowedKeys,
    label = '',
    error = '',
    disabled = false,
}: DynamicEquationEditorInnerProps) {
    const [editor] = useLexicalComposerContext();
    const lastJsonRef = useRef<string>('');
    const isHydratingRef = useRef<boolean>(false);
    const hasHydratedRef = useRef<boolean>(false);

    // Refs for popup positioning
    const editorContainerRef = useRef<HTMLDivElement>(null);
    const keypadPopupRef = useRef<HTMLDivElement>(null);
    const keypadHeightRef = useRef<number>(0);
    const [showKeypadPopup, setShowKeypadPopup] = useState(false);
    const [keypadReady, setKeypadReady] = useState(false);
    const [keypadPosition, setKeypadPosition] = useState<{
        top?: number;
        bottom?: number;
        left: number;
        width: number;
        maxHeight: number;
    }>({ left: 0, width: 0, maxHeight: 9999 });

    // Build allowed-char set
    const allowedSet = useMemo(() => {
        if (showKeypad) {
            return new Set(allowedKeys);
        } else {
            return new Set();
        }
    }, [allowedKeys, showKeypad]);
    const printableAllowed = useMemo(() => {
        if (allowedSet.size === 0) return null;
        const charMap = new Set<string>();

        const allSymbols: [string, string][] = [
            ...Object.entries(MathSymbols.numbers),
            ...Object.entries(MathSymbols.letters_lower),
            ...Object.entries(MathSymbols.letters_upper),
            ...Object.entries(MathSymbols.symbols),
        ];
        for (const [key, value] of allSymbols) {
            if (allowedSet.has(key)) {
                charMap.add(value);
            }
        }
        return charMap;
    }, [allowedSet]);
    const isFreeEnabled = useMemo(() => allowedSet.has('free'), [allowedSet]);

    const isAllowedChar = (char: string) => {
        if (isFreeEnabled) return true;
        if (printableAllowed === null) return true;
        return printableAllowed.has(char);
    };

    const cleanText = (text: string) => {
        if (isFreeEnabled) return text;
        if (printableAllowed === null) return text;
        return text
            .split('')
            .filter((c) => printableAllowed.has(c))
            .join('');
    };

    // Update keypad position when editor is focused
    const updateKeypadPosition = useCallback(
        (height: number) => {
            if (!editorContainerRef.current || !showKeypad) return;

            const rect = editorContainerRef.current.getBoundingClientRect();
            const GAP = 8;
            const spaceBelow = window.innerHeight - rect.bottom - GAP;
            const spaceAbove = rect.top - GAP;

            if (height > spaceBelow && spaceAbove > spaceBelow) {
                // Flip above only when there's room
                setKeypadPosition({
                    bottom: window.innerHeight - rect.top + GAP,
                    left: rect.left,
                    width: rect.width,
                    maxHeight: spaceAbove,
                });
            } else {
                // Default: place below
                setKeypadPosition({
                    top: rect.bottom + GAP,
                    left: rect.left,
                    width: rect.width,
                    maxHeight: spaceBelow,
                });
            }
        },
        [showKeypad]
    );

    // Handle focus/blur for showing/hiding keypad
    useEffect(() => {
        const handleFocus = () => {
            // Do not show keypad if only 'free' is allowed or if no setting is applied
            if (
                showKeypad &&
                !disabled &&
                allowedKeys.length &&
                (allowedKeys.length > 1 || allowedKeys[0] !== 'free')
            ) {
                setKeypadReady(false);
                setShowKeypadPopup(true);
            }
        };

        const handleBlur = (e: FocusEvent) => {
            // Don't hide if clicking within the keypad popup
            if (
                keypadPopupRef.current &&
                keypadPopupRef.current.contains(e.relatedTarget as Node)
            ) {
                return;
            }
            setShowKeypadPopup(false);
            setKeypadReady(false);
        };

        const container = editorContainerRef.current;
        if (container) {
            container.addEventListener('focusin', handleFocus);
            container.addEventListener('focusout', handleBlur);
        }

        return () => {
            if (container) {
                container.removeEventListener('focusin', handleFocus);
                container.removeEventListener('focusout', handleBlur);
            }
        };
    }, [showKeypad, disabled, updateKeypadPosition, allowedKeys]);

    // Update position on scroll/resize
    useEffect(() => {
        if (!showKeypadPopup) return;

        const handleScrollOrResize = () => {
            updateKeypadPosition(keypadHeightRef.current);
        };

        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);

        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [showKeypadPopup, updateKeypadPosition]);

    // Measure the popup's natural height and position it once rendered
    useEffect(() => {
        if (!showKeypadPopup || !keypadPopupRef.current) return;

        const el = keypadPopupRef.current;

        const measure = () => {
            // scrollHeight gives the full natural height regardless of any maxHeight cap
            const height = el.scrollHeight;
            if (height === 0) return;
            keypadHeightRef.current = height;
            updateKeypadPosition(height);
            setKeypadReady(true);
        };

        const ro = new ResizeObserver(measure);
        ro.observe(el);

        return () => ro.disconnect();
    }, [showKeypadPopup, updateKeypadPosition]);

    const isEditingKey = (e: KeyboardEvent) => {
        if (e.ctrlKey || e.metaKey || e.altKey) return true;
        const k = e.key;
        return (
            k === 'Backspace' ||
            k === 'Delete' ||
            k === 'ArrowLeft' ||
            k === 'ArrowRight' ||
            k === 'ArrowUp' ||
            k === 'ArrowDown' ||
            k === 'Home' ||
            k === 'End' ||
            k === 'Tab' ||
            k === 'Enter'
        );
    };
    useEffect(() => {
        if (!editor) return;

        const unsubKeyDown = editor.registerCommand(
            KEY_DOWN_COMMAND,
            (event: KeyboardEvent) => {
                if (isEditingKey(event)) {
                    return false;
                }

                const char = event.key;
                if (char.length !== 1) {
                    return false;
                }

                if (char === '-' && printableAllowed !== null && printableAllowed.has('–')) {
                    event.preventDefault(); // Stop the hyphen from being typed

                    editor.update(() => {
                        const selection = $getSelection();
                        if ($isRangeSelection(selection)) {
                            selection.insertText('–'); // Insert the en-dash instead
                        }
                    });
                    return true;
                }
                if (!isAllowedChar(char)) {
                    event.preventDefault();
                    return true;
                }

                return false;
            },
            COMMAND_PRIORITY_CRITICAL
        );

        const unsubPaste = editor.registerCommand(
            PASTE_COMMAND,
            (event: ClipboardEvent) => {
                if (isFreeEnabled || printableAllowed === null) {
                    return false;
                }

                const text = event.clipboardData?.getData('text/plain') ?? '';
                const cleaned = cleanText(text);

                if (cleaned !== text) {
                    event.preventDefault();

                    editor.update(() => {
                        const selection = $getSelection();
                        if ($isRangeSelection(selection)) {
                            selection.insertText(cleaned);
                        }
                    });
                }
                return true;
            },
            COMMAND_PRIORITY_CRITICAL
        );

        return () => {
            unsubKeyDown();
            unsubPaste();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, printableAllowed, allowedSet, isFreeEnabled]);

    useEffect(() => {
        if (!editor) return;
        editor.setEditable(!disabled);
    }, [editor, disabled]);

    // Hydration effect - runs once when contentKey is defined
    useEffect(() => {
        if (!editor || hasHydratedRef.current || contentKey === undefined) {
            return;
        }

        hasHydratedRef.current = true;
        const trimmed = (initialValue ?? '').trim();

        if (!trimmed) {
            return;
        }

        let decompressed = trimmed;
        try {
            decompressed = decompressEditorState(trimmed);
        } catch {
            console.error('Failed to decompress, trying raw:');
            decompressed = trimmed;
        }

        let parsedState = null;
        try {
            parsedState = JSON.parse(decompressed);
        } catch {
            // Try using Lexical's parser for malformed JSON
            try {
                const editorState = editor.parseEditorState(trimmed);
                isHydratingRef.current = true;
                editor.setEditorState(editorState);
                setTimeout(() => {
                    isHydratingRef.current = false;
                }, 100);
                return;
            } catch {
                // Fall through to HTML fallback
            }
        }

        if (parsedState && parsedState.root) {
            isHydratingRef.current = true;

            queueMicrotask(() => {
                try {
                    editor.update(
                        () => {
                            const currentRoot = $getRoot();
                            currentRoot.clear();

                            if (
                                parsedState.root.children &&
                                Array.isArray(parsedState.root.children)
                            ) {
                                parsedState.root.children.forEach((childData: any) => {
                                    if (childData.type === 'paragraph') {
                                        const para = $createParagraphNode();

                                        if (
                                            childData.children &&
                                            Array.isArray(childData.children)
                                        ) {
                                            childData.children.forEach((nodeData: any) => {
                                                if (nodeData.type === 'text') {
                                                    const textNode = $createTextNode(nodeData.text);
                                                    para.append(textNode);
                                                } else if (nodeData.type === 'fraction') {
                                                    const node = FractionNode.importJSON(
                                                        nodeData as any
                                                    );
                                                    para.append(node);
                                                    // Force decorator re-render
                                                    setTimeout(() => {
                                                        editor.update(() => {
                                                            const n = $getNodeByKey(node.getKey());
                                                            if (n) n.getWritable();
                                                        });
                                                    }, 0);
                                                } else if (nodeData.type === 'exponent') {
                                                    const node = ExponentNode.importJSON(
                                                        nodeData as any
                                                    );
                                                    para.append(node);
                                                    setTimeout(() => {
                                                        editor.update(() => {
                                                            const n = $getNodeByKey(node.getKey());
                                                            if (n) n.getWritable();
                                                        });
                                                    }, 0);
                                                } else if (nodeData.type === 'root') {
                                                    const node = RootNode.importJSON(
                                                        nodeData as any
                                                    );
                                                    para.append(node);
                                                    setTimeout(() => {
                                                        editor.update(() => {
                                                            const n = $getNodeByKey(node.getKey());
                                                            if (n) n.getWritable();
                                                        });
                                                    }, 0);
                                                } else if (nodeData.type === 'overline') {
                                                    const node = OverlineNode.importJSON(
                                                        nodeData as any
                                                    );
                                                    para.append(node);
                                                    setTimeout(() => {
                                                        editor.update(() => {
                                                            const n = $getNodeByKey(node.getKey());
                                                            if (n) n.getWritable();
                                                        });
                                                    }, 0);
                                                }
                                            });
                                        }

                                        currentRoot.append(para);
                                    }
                                });
                            }
                        },
                        { tag: 'history-merge' }
                    );

                    setTimeout(() => {
                        isHydratingRef.current = false;
                    }, 100);
                } catch (err) {
                    console.error('Error in hydration:', err);
                    isHydratingRef.current = false;
                }
            });
        } else {
            // Fallback -- empty editor
            isHydratingRef.current = true;

            editor.update(() => {
                const root = $getRoot();
                root.clear();
            });

            setTimeout(() => {
                isHydratingRef.current = false;
            }, 100);
        }
    }, [editor, contentKey, initialValue]);

    const handleChange = useCallback(
        (editorState: any) => {
            if (isHydratingRef.current) {
                return;
            }

            editorState.read(() => {
                const json = JSON.stringify(editorState.toJSON());
                if (lastJsonRef.current !== json) {
                    lastJsonRef.current = json;
                    if (setOutput) {
                        const output = isEquationEditorEmpty(json) ? '' : json;
                        const compressed = compressEditorState(output);
                        setOutput(compressed);
                    }
                }
            });
        },
        [setOutput]
    );

    return (
        <>
            {label && <label style={{ fontSize: '10pt' }}>{label}</label>}
            <div
                ref={editorContainerRef}
                className="pm-editor-surface"
                style={{
                    position: 'relative',
                    border: '2px solid #65686e',
                    padding: 8,
                    borderRadius: 6,
                }}
            >
                <RichTextPlugin
                    contentEditable={<ContentEditable />}
                    placeholder={
                        <div
                            style={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                pointerEvents: 'none',
                                color: '#9ca3af',
                                fontSize: 'var(--pm-math-font-size)',
                                userSelect: 'none',
                            }}
                        >
                            Enter your answer here...
                        </div>
                    }
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <HistoryPlugin />
                <BlockEditorEnterPlugin override={isFreeEnabled} />
                <ArrowKeyNavigationPlugin />
                <MathPlugin />
                <OnChangePlugin onChange={handleChange} />
            </div>

            {/* Floating keypad popup */}
            {showKeypad &&
                showKeypadPopup &&
                createPortal(
                    <div
                        ref={keypadPopupRef}
                        className="pm-keypad-popup"
                        style={{
                            position: 'fixed',
                            top:
                                keypadPosition.top !== undefined
                                    ? `${keypadPosition.top}px`
                                    : undefined,
                            bottom:
                                keypadPosition.bottom !== undefined
                                    ? `${keypadPosition.bottom}px`
                                    : undefined,
                            left: `${keypadPosition.left}px`,
                            width: `${keypadPosition.width}px`,
                            zIndex: 9999,
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: 6,
                            boxShadow:
                                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            padding: 8,
                            maxHeight: `${keypadPosition.maxHeight}px`,
                            visibility: keypadReady ? 'visible' : 'hidden',
                        }}
                        onMouseDown={(e) => {
                            // Prevent blur when clicking inside keypad
                            e.preventDefault();
                        }}
                    >
                        <MathKeypadPlugin disabled={disabled} />
                    </div>,
                    document.body
                )}

            <ErrorMessage error={error} />
        </>
    );
}
